import type { ComponentType, JSX } from 'react'
import { ViteReactSSG } from 'vite-react-ssg'
import i18n, { i18nReady } from './i18n'
import { prepareRenderLocale } from './i18n/renderLocale'
import { createMarketingBrowserRouter, STATIC_PAGE_HANDLE } from './lib/staticPageRoutes'
import './index.css'

// Analytics (PostHog) and ad pixels are NOT initialized here — they load only
// after the visitor accepts cookies. See analytics/initAnalytics.ts, gated by
// the CookieConsentProvider in Layout.tsx.

import { Layout } from './Layout'
import { LocaleRoute } from './components/LocaleRoute'
import NotFound from './pages/NotFound'
import { ANTALYA_DESTINATIONS } from './data/antalyaLocations'
import { ISTANBUL_DESTINATIONS } from './data/istanbulLocations'
import { MUGLA_DESTINATIONS } from './data/muglaLocations'

// Wrap every page in LocaleRoute so the URL's locale prefix syncs i18next.
// Without it, deep-linking to /tr/about would render Turkish content under
// another language selected by a previous navigation.
const wrap = (element: JSX.Element) => <LocaleRoute>{element}</LocaleRoute>

const LOCATION_PAGES = [
  { path: 'locations/mugla', load: () => import('./pages/MuglaLocations'), entry: 'src/pages/MuglaLocations.tsx' },
  ...MUGLA_DESTINATIONS.map(({ slug }) => ({
    path: `locations/mugla/${slug}`,
    load: () => import('./pages/MuglaLocationDetail'), entry: 'src/pages/MuglaLocationDetail.tsx',
  })),
  { path: 'locations/antalya', load: () => import('./pages/AntalyaLocations'), entry: 'src/pages/AntalyaLocations.tsx' },
  ...ANTALYA_DESTINATIONS.map(({ slug }) => ({
    path: `locations/antalya/${slug}`,
    load: () => import('./pages/AntalyaLocationDetail'), entry: 'src/pages/AntalyaLocationDetail.tsx',
  })),
  { path: 'locations/istanbul', load: () => import('./pages/IstanbulLocations'), entry: 'src/pages/IstanbulLocations.tsx' },
  ...ISTANBUL_DESTINATIONS.map(({ slug }) => ({
    path: `locations/istanbul/${slug}`,
    load: () => import('./pages/IstanbulLocationDetail'), entry: 'src/pages/IstanbulLocationDetail.tsx',
  })),
] as const

const ARTICLE_PAGES = {
  en: () => import('virtual:article-page/en'),
  tr: () => import('virtual:article-page/tr'),
  de: () => import('virtual:article-page/de'),
  es: () => import('virtual:article-page/es'),
  ru: () => import('virtual:article-page/ru'),
  el: () => import('virtual:article-page/el'),
}

const PAGES = [
  { path: '', load: () => import('./pages/Index'), entry: 'src/pages/Index.tsx' },
  { path: 'how-it-works', load: () => import('./pages/HowItWorks'), entry: 'src/pages/HowItWorks.tsx' },
  { path: 'why-docio', load: () => import('./pages/WhyDocio'), entry: 'src/pages/WhyDocio.tsx' },
  { path: 'doctor-visits', load: () => import('./pages/DoctorVisits'), entry: 'src/pages/DoctorVisits.tsx' },
  { path: 'pharmacy-finder', load: () => import('./pages/PharmacyFinder'), entry: 'src/pages/PharmacyFinder.tsx' },
  { path: 'resources', load: () => import('./pages/Resources'), entry: 'src/pages/Resources.tsx' },
  { path: 'free-tools', load: () => import('./pages/FreeTools'), entry: 'src/pages/FreeTools.tsx' },
  { path: 'allergy-card', load: () => import('./pages/AllergyCard'), entry: 'src/pages/AllergyCard.tsx' },
  { path: 'contact', load: () => import('./pages/Contact'), entry: 'src/pages/Contact.tsx' },
  { path: 'privacy-policy', load: () => import('./pages/PrivacyPolicy'), entry: 'src/pages/PrivacyPolicy.tsx' },
  { path: 'terms-of-service', load: () => import('./pages/TermsOfService'), entry: 'src/pages/TermsOfService.tsx' },
  { path: 'physician-terms', load: () => import('./pages/PhysicianTerms'), entry: 'src/pages/PhysicianTerms.tsx' },
  { path: 'insurance-terms', load: () => import('./pages/InsuranceTerms'), entry: 'src/pages/InsuranceTerms.tsx' },
  { path: 'legal', load: () => import('./pages/LegalHub'), entry: 'src/pages/LegalHub.tsx' },
  { path: 'about', load: () => import('./pages/About'), entry: 'src/pages/About.tsx' },
  { path: 'articles', load: () => import('./pages/Articles'), entry: 'src/pages/Articles.tsx' },
  { path: 'articles/:slug', load: ARTICLE_PAGES.en, entry: 'src/pages/ArticleDetail.tsx' },
  ...LOCATION_PAGES,
] as const

const PREFIXED_LOCALES = ['tr', 'de', 'es', 'ru', 'el'] as const

// Router lazy modules resolve before hydration/navigation; SSG still renders full HTML.
const loadPage = async (
  load: () => Promise<{ default: ComponentType }>,
  pathname: string,
) => {
  await i18nReady
  const [{ default: Page }] = await Promise.all([
    load(),
    prepareRenderLocale(i18n, pathname),
  ])
  return { Component: () => wrap(<Page />) }
}

const enRoutes = PAGES.map((p) =>
  p.path === ''
    ? { index: true as const, path: '/', lazy: () => loadPage(p.load, '/'), entry: p.entry, handle: STATIC_PAGE_HANDLE }
    : { path: `/${p.path}`, lazy: () => loadPage(p.load, `/${p.path}`), entry: p.entry, handle: STATIC_PAGE_HANDLE },
)

// vite-react-ssg pre-renders only routes declared here — prefixed-locale
// pages (/tr, /de/about, …) must live in this list or every non-English URL
// resolves to the catch-all NotFound.
const prefixedRoutes = PREFIXED_LOCALES.flatMap((lc) =>
  PAGES.map((p) => ({
    path: p.path ? `/${lc}/${p.path}` : `/${lc}`,
    lazy: () => loadPage(p.path === 'articles/:slug' ? ARTICLE_PAGES[lc] : p.load, p.path ? `/${lc}/${p.path}` : `/${lc}`),
    entry: p.entry,
    handle: STATIC_PAGE_HANDLE,
  })),
)

export const createRoot = ViteReactSSG(
  {
    customCreateRouter: createMarketingBrowserRouter,
    routes: [
      {
        element: <Layout />,
        handle: STATIC_PAGE_HANDLE,
        children: [
          ...enRoutes,
          ...prefixedRoutes,
          { path: '/404', element: wrap(<NotFound />), handle: STATIC_PAGE_HANDLE },
          { path: '*', element: wrap(<NotFound />), handle: STATIC_PAGE_HANDLE },
        ],
      },
    ],
  },
  async ({ routePath, router }) => {
    // SSG supplies routePath only on the server. Match the browser URL before
    // hydration too, so localized HTML is not first rendered in English.
    await i18nReady
    await prepareRenderLocale(i18n, routePath, router?.state.location.pathname)
  },
)
